import { useEffect, useRef } from 'react';

/**
 * Custom hook that executes a callback when the user returns to the tab
 * Only triggers if the user was away for longer than the specified threshold
 *
 * @param callback Function to execute when the tab becomes visible
 * @param thresholdMs Optional minimum time (in ms) the user must be away before callback triggers
 */
export function useTabFocus(callback: () => void, thresholdMs?: number): void {
  // Store the callback in a ref so we always have the latest version
  const callbackRef = useRef(callback);

  // Store the timestamp when the tab becomes hidden
  const tabHiddenTimeRef = useRef<number | null>(null);

  // Update the ref whenever the callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Set up the event listener only once
  useEffect(() => {
    const handleVisibilityChange = () => {
      // When tab becomes hidden, store the current timestamp
      if (document.visibilityState === 'hidden') {
        tabHiddenTimeRef.current = Date.now();
      }
      // When tab becomes visible again
      else if (document.visibilityState === 'visible') {
        // Only proceed if we have a stored timestamp
        if (tabHiddenTimeRef.current !== null) {
          const timeAway = Date.now() - tabHiddenTimeRef.current;

          // If no threshold is provided or time away exceeds threshold
          if (thresholdMs === undefined || timeAway >= thresholdMs) {
            callbackRef.current();
          }

          // Reset the timestamp
          tabHiddenTimeRef.current = null;
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // If the tab is already hidden when the component mounts, set the timestamp
    if (document.visibilityState === 'hidden') {
      tabHiddenTimeRef.current = Date.now();
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []); // Empty deps array - this effect runs only once
}
